/**
 * Slim banner that surfaces offline state and a brief "back online" pulse.
 *
 * Placed once at the top of the app shell so every route inherits it without
 * each page having to remember to render its own connectivity hint.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { CloudOff, Wifi } from 'lucide-react'
import { useOnlineStatus } from '@/hooks/pwa'
import { cn } from '@/lib/utils'

export function OfflineBanner() {
  const { isOnline, justReconnected } = useOnlineStatus()

  // Show the offline state, or the green reconnect pulse, but never both.
  const visible = !isOnline || justReconnected
  const variant: 'offline' | 'online' = !isOnline ? 'offline' : 'online'

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={variant}
          role="status"
          aria-live="polite"
          initial={{ y: -32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -32, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 32 }}
          className={cn(
            'sticky top-0 z-[60] flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium text-white',
            variant === 'offline' && 'bg-primary',
            variant === 'online' && 'bg-success',
          )}
        >
          {variant === 'offline' ? (
            <>
              <CloudOff className="h-3.5 w-3.5" />
              <span>You're offline. Changes will sync when you reconnect.</span>
            </>
          ) : (
            <>
              <Wifi className="h-3.5 w-3.5" />
              <span>Back online — syncing your changes.</span>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
